import { useApiClient } from '../../hooks/useApiClient'
import { useApi } from '@gravitate-js/the-armory'
import { getAPIURL } from '../../utils/api'
import { useQuery } from '@tanstack/react-query'
import type { MatchStatus } from './types/common'
import type { MatchOverviewResponse, MatchOverviewRequest } from './types/match'

// Date range interface for cleaner API
interface DateRange {
  start: Date
  end: Date
}

// Interface for processed match data - includes model config alongside match data
export interface MatchView {
  rows: unknown[]
  modelConfig?: Gravitate.ModelConfig
  [key: string]: unknown // Allow any other fields from API response
}

/**
 * Hook for simple match data fetching without business logic
 * Following Freight pattern - just combines API responses with minimal processing
 */
export const useMatches = () => {
  const $api = useApiClient()
  const api = useApi({ baseURLOverride: getAPIURL() })

  /**
   * Simplified match overview query
   * - Props are: openDateRange, approvedDateRange, voidedDateRange, filters
   * - Just combines the API responses with minimal processing
   */
  const useMatchOverviewQuery = (params: {
    openDateRange?: DateRange
    approvedDateRange?: DateRange
    voidedDateRange?: DateRange
    filters?: Record<string, unknown>
    activeStatus?: MatchStatus | 'all'
    enabled?: boolean
  }) => {
    const { openDateRange, approvedDateRange, voidedDateRange, filters = {}, activeStatus, enabled = true } = params

    return useQuery({
      queryKey: ['supplier_rec_match_overview', openDateRange, approvedDateRange, voidedDateRange, filters],
      queryFn: async () => {
        const queries = []

        // Add model config query to fetch alongside match data
        const modelConfigQuery = api.post('best_buy/get_model_config', {})

        // Open query if date range provided
        if (openDateRange) {
          const openQuery = api.post('supplier_rec/match/overview', {
            start_date: openDateRange.start.toISOString(),
            end_date: openDateRange.end.toISOString(),
            filter: {
              status: 'open',
              ...filters, // spread filters as per Zac's example
            },
            include_params: true, // Request filter params metadata for GraviGrid
          })
          queries.push(openQuery)
        }

        if (approvedDateRange) {
          const approvedQuery = api.post('supplier_rec/match/overview', {
            start_date: approvedDateRange.start.toISOString(),
            end_date: approvedDateRange.end.toISOString(),
            filter: {
              // Keep status filter for approved since that's specific
              status: 'approved',
              ...filters, // spread filters
            },
            include_params: true, // Request filter params metadata for GraviGrid
          })
          queries.push(approvedQuery)
        }

        if (voidedDateRange) {
          const voidedQuery = api.post('supplier_rec/match/overview', {
            start_date: voidedDateRange.start.toISOString(),
            end_date: voidedDateRange.end.toISOString(),
            filter: {
              // Query for void status items (3-month range per SR-435)
              status: 'void',
              ...filters, // spread filters
            },
            include_params: true, // Request filter params metadata for GraviGrid
          })
          queries.push(voidedQuery)
        }

        // Always include model config query and execute all queries
        const allQueries = [modelConfigQuery, ...queries]
        const allResponses = await Promise.all(allQueries)

        // First response is always model config
        const rawModelConfig = allResponses[0]

        // Transform model config to ensure it has the expected supplier_reconciliation structure
        const modelConfig: Gravitate.ModelConfig = {
          ...rawModelConfig,
          supplier_reconciliation: rawModelConfig?.supplier_reconciliation || {
            delivery_reconciliation_mode: rawModelConfig?.enable_delivery_reconciliation
              ? 'block_approval'
              : 'does_not_block_approval',
          },
        }
        const matchResponses = allResponses.slice(1)

        // Handle match data responses
        if (matchResponses.length === 0) {
          // No match queries, just return model config
          return {
            rows: [],
            modelConfig,
          }
        } else if (matchResponses.length === 1) {
          // Single match query
          const response = matchResponses[0]
          const mappedRows = response?.rows || []
          return {
            ...response,
            rows: mappedRows,
            modelConfig,
          }
        } else {
          // Multiple match queries - combine results
          const firstResponse = matchResponses[0] || {}
          const allRows = matchResponses.flatMap((res) => res?.rows || [])

          // Deduplicate by ID (no status mapping needed)
          const seenIds = new Set()
          const combinedRows = allRows.filter((row) => {
            if (seenIds.has(row.id)) {
              return false // Skip duplicate
            }
            seenIds.add(row.id)
            return true
          })

          return {
            ...firstResponse,
            rows: combinedRows,
            modelConfig,
          }
        }
      },
      enabled, // Allow conditional query execution
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
      refetchOnMount: false, // Don't refetch when component mounts if data exists
      refetchOnReconnect: false, // Don't refetch on network reconnect
    })
  }

  /**
   * Independent query hook for open/other tabs data
   * Only recalculates when open tab date range changes
   */
  const useMatchOverviewOpenQuery = (params: {
    dateRange?: DateRange
    filters?: Record<string, unknown>
    enabled?: boolean
  }) => {
    const { dateRange, filters = {}, enabled = true } = params

    return useQuery({
      queryKey: ['supplier_rec_match_overview_open', dateRange, filters],
      queryFn: async () => {
        if (!dateRange) {
          return { rows: [], modelConfig: null }
        }

        const [modelConfigResponse, matchResponse] = await Promise.all([
          api.post('best_buy/get_model_config', {}),
          api.post('supplier_rec/match/overview', {
            start_date: dateRange.start.toISOString(),
            end_date: dateRange.end.toISOString(),
            filter: {
              status: 'open',
              ...filters,
            },
            include_params: true,
          }),
        ])

        const modelConfig: Gravitate.ModelConfig = {
          ...modelConfigResponse,
          supplier_reconciliation: modelConfigResponse?.supplier_reconciliation || {
            delivery_reconciliation_mode: modelConfigResponse?.enable_delivery_reconciliation
              ? 'block_approval'
              : 'does_not_block_approval',
          },
        }

        return {
          ...matchResponse,
          rows: matchResponse?.rows || [],
          modelConfig,
        }
      },
      enabled: enabled && !!dateRange,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
    })
  }

  /**
   * Independent query hook for approved tab data
   * Only recalculates when approved tab date range changes
   */
  const useMatchOverviewApprovedQuery = (params: {
    dateRange?: DateRange
    filters?: Record<string, unknown>
    enabled?: boolean
  }) => {
    const { dateRange, filters = {}, enabled = true } = params

    return useQuery({
      queryKey: ['supplier_rec_match_overview_approved', dateRange, filters],
      queryFn: async () => {
        if (!dateRange) {
          return { rows: [], modelConfig: null }
        }

        const [modelConfigResponse, matchResponse] = await Promise.all([
          api.post('best_buy/get_model_config', {}),
          api.post('supplier_rec/match/overview', {
            start_date: dateRange.start.toISOString(),
            end_date: dateRange.end.toISOString(),
            filter: {
              status: 'approved',
              ...filters,
            },
            include_params: true,
          }),
        ])

        const modelConfig: Gravitate.ModelConfig = {
          ...modelConfigResponse,
          supplier_reconciliation: modelConfigResponse?.supplier_reconciliation || {
            delivery_reconciliation_mode: modelConfigResponse?.enable_delivery_reconciliation
              ? 'block_approval'
              : 'does_not_block_approval',
          },
        }

        return {
          ...matchResponse,
          rows: matchResponse?.rows || [],
          modelConfig,
        }
      },
      enabled: enabled && !!dateRange,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
    })
  }

  /**
   * Independent query hook for voided tab data
   * Only recalculates when voided tab date range changes
   */
  const useMatchOverviewVoidedQuery = (params: {
    dateRange?: DateRange
    filters?: Record<string, unknown>
    enabled?: boolean
  }) => {
    const { dateRange, filters = {}, enabled = true } = params

    return useQuery({
      queryKey: ['supplier_rec_match_overview_voided', dateRange, filters],
      queryFn: async () => {
        if (!dateRange) {
          return { rows: [], modelConfig: null }
        }

        const [modelConfigResponse, matchResponse] = await Promise.all([
          api.post('best_buy/get_model_config', {}),
          api.post('supplier_rec/match/overview', {
            start_date: dateRange.start.toISOString(),
            end_date: dateRange.end.toISOString(),
            filter: {
              status: 'void',
              ...filters,
            },
            include_params: true,
          }),
        ])

        const modelConfig: Gravitate.ModelConfig = {
          ...modelConfigResponse,
          supplier_reconciliation: modelConfigResponse?.supplier_reconciliation || {
            delivery_reconciliation_mode: modelConfigResponse?.enable_delivery_reconciliation
              ? 'block_approval'
              : 'does_not_block_approval',
          },
        }

        return {
          ...matchResponse,
          rows: matchResponse?.rows || [],
          modelConfig,
        }
      },
      enabled: enabled && !!dateRange,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
    })
  }

  /**
   * Hook for fetching detailed match data by ID
   * Simple API call without business logic
   */
  const useMatchByIdQuery = (matchId: string | undefined) => {
    return $api.useQuery(
      'post',
      '/supplier_rec/match/overview',
      {
        body: {
          start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          end_date: new Date().toISOString(),
          match_id: matchId,
          filter: {},
        },
      },
      {
        enabled: !!matchId,
        retry: false, // Don't retry on 422 errors
      }
    )
  }

  /**
   * Simplified match overview query for only open matches
   * - No merging of multiple date ranges
   * - Always fetches open status only
   * - Still includes model config
   */
  const useMatchOverviewOnlyOpenQuery = (params: {
    dateRange: DateRange
    filters?: Record<string, unknown>
  }) => {
    const { dateRange, filters = {} } = params

    return useQuery({
      queryKey: ['supplier_rec_match_overview_only_open', dateRange, filters],
      queryFn: async () => {
        // Fetch model config and open matches in parallel
        const [modelConfigResponse, matchResponse] = await Promise.all([
          api.post('best_buy/get_model_config', {}),
          api.post('supplier_rec/match/overview', {
            start_date: dateRange.start.toISOString(),
            end_date: dateRange.end.toISOString(),
            filter: {
              status: 'open',
              ...filters,
            },
          }),
        ])

        // Transform model config to ensure it has the expected supplier_reconciliation structure
        const modelConfig: Gravitate.ModelConfig = {
          ...modelConfigResponse,
          supplier_reconciliation: modelConfigResponse?.supplier_reconciliation || {
            delivery_reconciliation_mode: modelConfigResponse?.enable_delivery_reconciliation
              ? 'block_approval'
              : 'does_not_block_approval',
          },
        }

        // Return combined response
        return {
          ...matchResponse,
          rows: matchResponse?.rows || [],
          modelConfig,
        }
      },
      refetchOnWindowFocus: false,
    })
  }

  return {
    useMatchOverview: useMatchOverviewQuery,
    useMatchOverviewOpen: useMatchOverviewOpenQuery,
    useMatchOverviewApproved: useMatchOverviewApprovedQuery,
    useMatchOverviewVoided: useMatchOverviewVoidedQuery,
    useMatchOverviewOnlyOpen: useMatchOverviewOnlyOpenQuery,
    useMatchById: useMatchByIdQuery,
  }
}
